import { useEffect } from "react";

import classNames from "classnames";
import dompurify from "isomorphic-dompurify";

import { Button } from "@jti/core-components/components/Button";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";
import { CloseButton } from "@jti/ui/components/CloseButton/CloseButton";
import { Dialog } from "@jti/ui/components/Dialog/Dialog";
import { useDeviceBreakpoint } from "@jti/ui/hooks/useDeviceBreakpoint/useDeviceBreakpoint";

import { XSellingDialogProductCard, type XSellingDialogProductCardProps } from "../XSellingDialogProductCard";

import styles from "./XSellingDialog.module.scss";

const { sanitize } = dompurify;

export type XSellingDialogProps = {
  isOpened: boolean;
  onConfirm: () => void;
  onClose: () => void;
  title: string;
  subtitle?: string;
  confirmButtonText: string;
  cancelButtonText: string;
  products: XSellingDialogProductCardProps[];
  showQuickAddToCartButton?: boolean;
};

export const X_SELLING_DIALOG_GTM_VIEW_LOCATION = "XSellingDialog";

const stripHtmlTags = (htmlString: string) => sanitize(htmlString, { ALLOWED_TAGS: [] });

const getProductBodyVariantStyles = (numberOfProducts: number) => {
  switch (numberOfProducts) {
    case 1:
      return styles.bodyVariantSingle;
    case 2:
      return styles.bodyVariantDouble;
    case 3:
      return styles.bodyVariantTriple;
    default:
      return styles.bodyVariantTriple;
  }
};

export const XSellingDialog = ({
  title,
  subtitle,
  confirmButtonText,
  cancelButtonText,
  products,
  isOpened,
  onClose,
  onConfirm,
  showQuickAddToCartButton = false,
}: XSellingDialogProps) => {
  const { breakpoint } = useDeviceBreakpoint();
  const buttonSize = breakpoint === "mobile" ? "md" : "lg";

  useEffect(() => {
    if (isOpened) {
      dispatchTrackingEvent(TrackingEvents.TrackingPopinView, {
        acknowledgement_status: "acknowledged",
        popin_name: title,
        popin_location: X_SELLING_DIALOG_GTM_VIEW_LOCATION,
      });
    }
  }, [isOpened, title]);

  return (
    <Dialog
      isOpened={isOpened}
      onClose={onClose}
      fullscreen={breakpoint === "mobile"}
      className={styles.dialog}
      closable={false}
      closeStyle={styles.closeButton}
      dataTestId="xselling-dialog"
      noPadding
      withContentPadding={false}
    >
      <div className={styles.container}>
        <CloseButton onClick={onClose} className={styles.closeButton} />

        <div className={styles.header}>
          <h2 className={styles.title}>{title}</h2>
          <p className={styles.subtitle}>{subtitle}</p>
        </div>

        <div className={classNames(styles.body, getProductBodyVariantStyles(products.length))}>
          {products.map((product, index) => (
            <XSellingDialogProductCard
              key={index}
              sku={product.sku}
              name={stripHtmlTags(product.name)}
              productUrl={product.productUrl}
              description={stripHtmlTags(product.description)}
              imgUrl={product.imgUrl}
              imgAlt={product.imgAlt}
              isSimpleProduct={product.isSimpleProduct}
              isInStock={product.isInStock}
              showQuickAddToCartButton={showQuickAddToCartButton}
            />
          ))}
        </div>

        <div className={styles.footer}>
          <Button variant="primary" size={buttonSize} onClick={onConfirm}>
            {confirmButtonText}
          </Button>
          <Button variant="secondary" size={buttonSize} onClick={onClose}>
            {cancelButtonText}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};
